<template>
  <v-container class="mt-4">
    <AppLoadingPage v-if="$apollo.loading" />

    <template v-else>
      <div class="d-flex justify-end">
        <v-col
          v-if="$allowedMutation(horse.allowedMutations, 'horseDocumentAdd')"
          md="6"
        >
          <v-file-input
            v-model="newDocument"
            :label="$t('common:fileUpload.label', 'File upload')"
            variant="outlined"
            hide-details="auto"
          />
          <div class="text-right">
            <v-btn
              :disabled="!newDocument || uploading"
              :loading="uploading"
              variant="outlined"
              color="edit"
              class="mt-4"
              @click="uploadDoc"
            >
              <v-icon size="small" start> $iconUpload </v-icon
              >{{ $t("common:common.uploaddocument.label") }}
            </v-btn>
          </div>
        </v-col>
      </div>
      <div
        v-if="applicationDocuments.length + horse.documents.length == 0"
        class="text-center"
      >
        {{
          $t("horses:documents.foundNoDocuments.message", "No documents found")
        }}
      </div>

      <AppFilesGrid :files="horse.documents">
        <template #actions="{ file: doc }">
          <v-btn-icon
            v-if="
              $allowedMutation(horse.allowedMutations, 'horseDocumentDelete')
            "
            icon
            size="small"
            class="mx-0"
            color="remove"
            :aria-label="$t('common:documentDeleteButton.alt')"
            :loading="mutationId == doc.id"
            :disabled="deleting"
            @click="deleteDoc(doc.id)"
          >
            <v-icon color="white" size="small"> $iconTrash </v-icon>

            <template #loader>
              <v-progress-circular color="white" size="21" indeterminate />
            </template>
          </v-btn-icon>
        </template>
      </AppFilesGrid>

      <section>
        <div v-for="app in applicationDocuments" :key="app.id" class="mb-3">
          <h1 class="mt-4 mb-2">
            {{
              $t(
                "horses:horseApplication.applicationTypes." +
                  app.applicationType
              )
            }}
            {{ $dayjs.date(app.createdAt) }}
          </h1>

          <template
            v-for="[documentKey, documents] in horseDocumentsOrdered(app)"
          >
            <div v-if="documents.length > 0" :key="documentKey" class="mb-4">
              <h2 class="mb-4">
                {{ horseDocumentCategoryLabels[documentKey] }}
              </h2>
              <AppFilesGrid :files="documents" />
            </div>
          </template>
        </div>
      </section>
    </template>
  </v-container>
</template>
<script>
import gql from "fraql";
import AppLoadingPage from "@elements/AppLoadingPage.vue";
import AppFilesGrid from "@elements/AppFilesGrid.vue";
import { useHorseApplicationDocumentsHelper } from "@composables/horseApplicationDocumentsHelper";
export default {
  name: "HorseDocuments",
  components: { AppLoadingPage, AppFilesGrid },
  props: { horseId: { type: String, required: true } },
  setup() {
    const {
      horseDocumentCategoryLabels,
      horseDocumentsOrdered,
      horseDocumentKeys
    } = useHorseApplicationDocumentsHelper();

    return {
      horseDocumentCategoryLabels,
      horseDocumentsOrdered,
      horseDocumentKeys
    };
  },
  data() {
    return {
      horseApplications: [],
      horse: {},
      addDocDialog: false,
      uploading: false,
      deleting: false,
      newDocument: null,
      mutationId: null
    };
  },
  computed: {
    applicationDocuments() {
      return this.horseApplications.filter(
        app =>
          app &&
          this.horseDocumentKeys.some(key => app[key] && app[key].length > 0)
      );
    }
  },
  apollo: {
    horseApplications: {
      query: gql`
        query horseApplications($horseIds: [ID]) {
          horseApplications(horseIds: $horseIds, statuses: ["APPROVED"]) {
            id
            applicationType
            createdAt
            attachedNatPassportNamePages {
              id
              ${AppFilesGrid.fragments.attachedFileFields}
            }
            attachedNatPassportInfoPages {
              id
              ${AppFilesGrid.fragments.attachedFileFields}
            }
            attachedNatPassportDiagramPages {
              id
              ${AppFilesGrid.fragments.attachedFileFields}
            }
            attachedNatPassportPedigreePages {
              id
              ${AppFilesGrid.fragments.attachedFileFields}
            }
            attachedNatPassportOwnerPages {
              id
              ${AppFilesGrid.fragments.attachedFileFields}
            }
            attachedOwnershipProofs {
              id
              ${AppFilesGrid.fragments.attachedFileFields}
            }
            attachedFullDocuments {
              id
              ${AppFilesGrid.fragments.attachedFileFields}
            }
            attachedFeiNamePages {
              id
              ${AppFilesGrid.fragments.attachedFileFields}
            }
            attachedFeiOwnerPages {
              id
              ${AppFilesGrid.fragments.attachedFileFields}
            }
            attachedPonyMeasurementProofs {
              id
              ${AppFilesGrid.fragments.attachedFileFields}
            }
            attachedVaccinationProofs {
              id
              ${AppFilesGrid.fragments.attachedFileFields}
            }
            documents {
              ${AppFilesGrid.fragments.attachedFileFields}
            }
          }
        }
      `,
      variables() {
        return { horseIds: [this.horseId] };
      }
    },
    horse: {
      query: gql`
        query horse($id: ID) {
          horse(id: $id) {
            id
            documents {
              ${AppFilesGrid.fragments.uploadedFileFields}
            }
            allowedMutations
          }
        }
      `,
      variables() {
        return { id: this.horseId };
      }
    }
  },
  methods: {
    async uploadDoc() {
      this.uploading = true;
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation horseDocumentAdd($horseId: ID!, $document: Upload!) {
              horseDocumentAdd(
                input: { horseId: $horseId, document: $document }
              ) {
                horse {
                  id
                  documents {
                    ${AppFilesGrid.fragments.uploadedFileFields}
                  }
                }
              }
            }
          `,
          variables: { horseId: this.horseId, document: this.newDocument }
        });
        this.addDocDialog = false;
        this.$root.$snackbar("success");
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.uploading = false;
      }
    },
    async deleteDoc(docId) {
      let userResponse = await this.$root.$confirm(
        this.$t("horses:documents.delete.heading"),
        this.$t("horses:documents.delete.question.text")
      );
      if (userResponse == true) {
        this.deleting = true;
        this.mutationId = docId;
        try {
          await this.$apollo.mutate({
            mutation: gql`
              mutation horseDocumentDelete($horseId: ID!, $documentId: ID!) {
                horseDocumentDelete(
                  input: { horseId: $horseId, documentId: $documentId }
                ) {
                  horse {
                    id
                    documents {
                      ${AppFilesGrid.fragments.uploadedFileFields}
                    }
                  }
                }
              }
            `,
            variables: { horseId: this.horseId, documentId: docId }
          });
          this.$root.$snackbar("success");
        } catch (error) {
          this.$root.$snackbarServerError(error, { level: "error" });
        } finally {
          this.deleting = false;
        }
      }
    }
  }
};
</script>
